import {surveyControls} from "../runner/surveyRunnerColors";
import {Box, Divider, IconButton, Link, Menu, MenuItem, Stack} from "@mui/material";
import {alpha} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import {LocalizedText} from "crqlar-react-common/dist/models/common/localizedText.model";
import {getIconForLanguage, getNameForLanguage} from "crqlar-react-common/dist/utils/translations.util";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import crqlarMark from "../../../assets/media/images/crqlar-logo.png";
import {SurveyRunnerLocalize} from "../runner/questions/runnerQuestion.types";

type FooterLinks = {
    privacyPolicyUrl?: LocalizedText
    termsUrl?: LocalizedText
    imprintUrl?: LocalizedText
    contactUrl?: LocalizedText
};

interface Props {
    survey: FooterLinks
    localize: SurveyRunnerLocalize
    language: string
    organizationName?: string
    languages?: string[]
    onLanguageChange?: (language: string) => void
}

const FLAG_WIDTH = 21;
const FLAG_HEIGHT = 14;

const MARK_SIZE = 12;

const FOOTER_FONT_SIZE = 14;

const BRANDING_FONT_SIZE = 12;

function PublicSurveyFooter({survey, localize, language, organizationName, languages, onLanguageChange}: Props) {
    const {t} = useTranslation(undefined, {lng: language});

    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

    const links = [
        {label: t('Privacy'), url: localize(survey.privacyPolicyUrl)},
        {label: t('Terms'), url: localize(survey.termsUrl)},
        {label: t('Imprint'), url: localize(survey.imprintUrl)},
        {label: t('Contact'), url: localize(survey.contactUrl)},
    ].filter(entry => !!entry.url);

    const offered = languages ?? [];
    const showLanguageMenu = offered.length > 1 && !!onLanguageChange;

    const flag = (code: string) => {
        const src = getIconForLanguage(code);

        if (!src) {
            return (
                <Typography sx={{fontSize: FOOTER_FONT_SIZE, fontWeight: 600, color: 'inherit'}}>
                    {code.toUpperCase()}
                </Typography>
            );
        }

        return (
            <Box
                component={'img'}
                src={src}
                alt={''}
                sx={theme => ({
                    display: 'block',
                    flexShrink: 0,
                    boxSizing: 'border-box',
                    width: FLAG_WIDTH,
                    height: FLAG_HEIGHT,
                    objectFit: 'cover',
                    borderRadius: 0.5,
                    border: `1px solid ${alpha(theme.palette.text.primary, 0.15)}`,
                })}
            />
        );
    };

    const languageMenu = showLanguageMenu && (
        <>
            <IconButton
                aria-label={t('Change language')}
                onClick={(event) => setMenuAnchor(event.currentTarget)}
                sx={{
                    p: 0.5,
                    color: 'text.secondary',
                    transition: 'color 150ms ease',
                    '&:hover': {color: theme => surveyControls(theme).accent},
                }}
            >
                {flag(language)}
            </IconButton>

            <Menu
                anchorEl={menuAnchor}
                open={!!menuAnchor}
                onClose={() => setMenuAnchor(null)}
                anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                transformOrigin={{vertical: 'bottom', horizontal: 'center'}}
            >
                {offered.map(code => (
                    <MenuItem
                        key={code}
                        selected={code === language}
                        onClick={() => {
                            onLanguageChange?.(code);
                            setMenuAnchor(null);
                        }}
                    >
                        <Stack direction={'row'} alignItems={'center'} gap={1}>
                            {flag(code)}
                            <Typography sx={{fontSize: FOOTER_FONT_SIZE}}>
                                {getNameForLanguage(code, true, true) || code.toUpperCase()}
                            </Typography>
                        </Stack>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );

    const branding = (
        <Stack
            component={'a'}
            href={'https://crqlar.com'}
            target={'_blank'}
            rel={'noopener noreferrer'}
            direction={'row'}
            alignItems={'center'}
            gap={0.75}
            sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                transition: 'color 150ms ease',
                '&:hover': {color: theme => surveyControls(theme).accent},
            }}
        >
            <Typography sx={{fontSize: BRANDING_FONT_SIZE, color: 'inherit'}}>
                {t('Powered by Crqlar.com')}
            </Typography>

            <Box
                component={'img'}
                src={crqlarMark}
                alt={''}
                sx={{
                    display: 'block',
                    width: MARK_SIZE,
                    height: MARK_SIZE,
                    flexShrink: 0,
                }}
            />
        </Stack>
    );

    return (
        <>
            <Divider/>

            <Stack gap={1.5} alignItems={'center'} sx={{width: '100%'}}>
                <Stack
                    direction={'row'}
                    flexWrap={'wrap'}
                    gap={2}
                    justifyContent={'center'}
                    alignItems={'center'}
                >
                    {!!organizationName && (
                        <Typography variant={'body2'} color={'text.secondary'} sx={{fontWeight: 600}}>
                            {organizationName}
                        </Typography>
                    )}

                    {links.map(entry => (
                        <Link
                            key={entry.label}
                            href={entry.url}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                            variant={'body2'}
                            color={'text.secondary'}
                            sx={{
                                textDecorationColor: 'currentcolor',
                                transition: 'color 150ms ease',
                                '&:hover': {color: theme => surveyControls(theme).accent},
                            }}
                        >
                            {entry.label}
                        </Link>
                    ))}

                    {languageMenu}
                </Stack>

                {branding}
            </Stack>
        </>
    );
}

export default PublicSurveyFooter;
